import React, { useCallback } from "react";
import Spinner from "components/spinner";
import { OutlineButton } from "components/button";
import { enUS, es } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "contexts/auth";
import { useSnackbarContext } from "components/snackbar";

import { Props } from "./statement/types";
import { extractCompanyDetails } from "./statement/utils";
import { useStatementDateFilter } from "./statement/hooks/useStatementDateFilter";
import { useStatementInvoices } from "./statement/hooks/useStatementInvoices";
import { useStatementEmail } from "./statement/hooks/useStatementEmail";

import StatementPrintStyles from "./statement/components/StatementPrintStyles";
import StatementHeaderActions from "./statement/components/StatementHeaderActions";
import StatementFilterToolbar from "./statement/components/StatementFilterToolbar";
import StatementCustomerHeader from "./statement/components/StatementCustomerHeader";
import StatementSummaryCards from "./statement/components/StatementSummaryCards";
import StatementTableList from "./statement/components/StatementTableList";

export default function CustomerStatementModal({
  customerId,
  customerInfo,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const tBase = "views.customer.component.statement";
  const tr = useCallback((key: string) => t(`${tBase}.${key}`, key), [t]);
  const dateLocale = i18n?.language === "es" || i18n?.language?.startsWith("es") ? es : enUS;

  const { cvd_type, businessName } = useAuthContext();
  const snackbar = useSnackbarContext();
  const showSnackbar = snackbar?.showSnackbar || ((_msg: any) => {});

  // Date filter state hook
  const dateFilterHook = useStatementDateFilter({ tr, dateLocale });

  // Invoices fetching, filtering, and grouping hook
  const invoicesHook = useStatementInvoices({
    customerId,
    customerInfo,
    tr,
    dateFilterHook,
  });

  // Email collection, selection, and sending hook
  const emailHook = useStatementEmail({
    customerId,
    customerData: invoicesHook.customerData,
    customerEmail: invoicesHook.customerEmail,
    tr,
    showSnackbar,
  });

  // Company details derived from companySettingsQuery
  const {
    companyLogo,
    companyName,
    companyAddress,
    companyCityStateZip,
    companyPhone,
    companyEmail,
  } = extractCompanyDetails(invoicesHook.companySettingsQuery.data, businessName);

  const handlePrint = () => {
    if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
      try {
        window.scrollTo(0, 0);
      } catch (e) {
        // Ignore in test environments where scrollTo is not fully implemented
      }
    }
    window.print();
  };

  const handleSendEmail = (emails: string[]) => {
    emailHook.handleSendEmail({
      invoice_ids: invoicesHook.filteredInvoices.map((invoice) => invoice.id),
    }, emails.join(", "));
  };

  return (
    <div className="statement-print-wrapper fixed inset-0 z-[1000] overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 print:p-0 print:bg-white print:static print:overflow-visible">
      {/* Print Styles */}
      <StatementPrintStyles />

      <div className="statement-modal-content bg-white w-full max-w-[90vw] rounded-xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] print:max-h-none print:shadow-none print:border-none print:rounded-none">
        {/* Top Action Header */}
        <StatementHeaderActions
          customerName={invoicesHook.customerData?.name}
          selectedEmail={emailHook.selectedEmail}
          setSelectedEmail={emailHook.setSelectedEmail}
          isEmailDropdownOpen={emailHook.isEmailDropdownOpen}
          setIsEmailDropdownOpen={emailHook.setIsEmailDropdownOpen}
          customerRegisteredEmails={emailHook.customerRegisteredEmails}
          isSendingEmail={emailHook.isSendingEmail}
          isEmailCooldownActive={emailHook.isEmailCooldownActive}
          formattedCooldownTime={emailHook.formattedCooldownTime}
          emailDropdownRef={emailHook.emailDropdownRef}
          emailInputRef={emailHook.emailInputRef}
          handleClearEmail={emailHook.handleClearEmail}
          handleSelectEmail={emailHook.handleSelectEmail}
          onSendEmail={handleSendEmail}
          onPrint={handlePrint}
          onClose={onClose}
          tr={tr}
          isStatementError={invoicesHook.statementQuery.isError}
          isStatementEmpty={invoicesHook.filteredInvoices.length === 0}
        />

        {/* Filter Toolbar */}
        <StatementFilterToolbar
          onlyOutstanding={invoicesHook.onlyOutstanding}
          setOnlyOutstanding={invoicesHook.setOnlyOutstanding}
          dateFilter={dateFilterHook.dateFilter}
          setDateFilter={dateFilterHook.setDateFilter}
          customStartDate={dateFilterHook.customStartDate}
          setCustomStartDate={dateFilterHook.setCustomStartDate}
          customEndDate={dateFilterHook.customEndDate}
          setCustomEndDate={dateFilterHook.setCustomEndDate}
          isDateDropdownOpen={dateFilterHook.isDateDropdownOpen}
          setIsDateDropdownOpen={dateFilterHook.setIsDateDropdownOpen}
          dateDropdownRef={dateFilterHook.dateDropdownRef}
          dateFilterOptions={dateFilterHook.dateFilterOptions}
          activeDateLabel={dateFilterHook.activeDateLabel}
          handleClearCustomDates={dateFilterHook.handleClearCustomDates}
          invoiceStatusFilters={invoicesHook.invoiceStatusFilters}
          setInvoiceStatusFilters={invoicesHook.setInvoiceStatusFilters}
          isStatusDropdownOpen={invoicesHook.isStatusDropdownOpen}
          setIsStatusDropdownOpen={invoicesHook.setIsStatusDropdownOpen}
          statusDropdownRef={invoicesHook.statusDropdownRef}
          availableStatuses={invoicesHook.availableStatuses}
          statusCounts={invoicesHook.statusCounts}
          handleSelectStatus={invoicesHook.handleSelectStatus}
          totalInvoicesCount={invoicesHook.allInvoices.length}
          invoiceTypeFilter={invoicesHook.invoiceTypeFilter}
          setInvoiceTypeFilter={invoicesHook.setInvoiceTypeFilter}
          isTypeDropdownOpen={invoicesHook.isTypeDropdownOpen}
          setIsTypeDropdownOpen={invoicesHook.setIsTypeDropdownOpen}
          typeDropdownRef={invoicesHook.typeDropdownRef}
          typeFilterOptions={invoicesHook.typeFilterOptions}
          activeTypeOption={invoicesHook.activeTypeOption}
          searchTerm={invoicesHook.searchTerm}
          setSearchTerm={invoicesHook.setSearchTerm}
          tr={tr}
        />

        {/* Modal Scrollable Body */}
        <div className="statement-scrollable-body flex-1 overflow-y-auto p-6 space-y-6 print:overflow-visible print:p-0">
          {invoicesHook.statementQuery.isLoading ? (
            <div className="py-12 flex justify-center">
              <Spinner />
            </div>
          ) : invoicesHook.statementQuery.isError ? (
            <div className="py-12 text-center text-sm text-red-600">
              {tr("Could not load invoices for this statement. Please try again.")}
            </div>
          ) : (
            <>
              {/* Statement Customer & Company Header */}
              <StatementCustomerHeader
                companyLogo={companyLogo}
                companyName={companyName}
                companyAddress={companyAddress}
                companyCityStateZip={companyCityStateZip}
                companyPhone={companyPhone}
                companyEmail={companyEmail}
                customerData={invoicesHook.customerData}
                customerEmail={invoicesHook.customerEmail}
                customerPhone={invoicesHook.customerPhone}
                dateLocale={dateLocale}
                tr={tr}
              />

              {/* Financial Totals Summary Cards */}
              <StatementSummaryCards
                overallTotals={invoicesHook.overallTotals}
                tr={tr}
              />

              {/* Invoices Grouped by Request / Project */}
              <StatementTableList
                groupedInvoices={invoicesHook.groupedInvoices}
                onlyOutstanding={invoicesHook.onlyOutstanding}
                dateLocale={dateLocale}
                cvd_type={cvd_type}
                tr={tr}
              />
            </>
          )}
        </div>

        {/* Modal Bottom Footer */}
        <div className="no-print bg-slate-50 border-t border-slate-200 px-6 py-3 flex justify-between items-center shrink-0">
          <p className="text-xs text-slate-500 m-0">
            {tr("Showing")} {invoicesHook.filteredInvoices.length} {tr("invoice(s)")}
          </p>
          <OutlineButton type="button" onClick={onClose}>
            {tr("Close")}
          </OutlineButton>
        </div>
      </div>
    </div>
  );
}
